import { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_leo-carter-impact/artifacts/7nvj4c0n_LEO%20CLUB%20OF%20MUMBAI%20CARTER%20ROAD%20%281%29.png";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/events", label: "Events" },
  { to: "/leadership", label: "Leadership" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location]);

  return (
    <header
      className={`main-nav fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-black/5"
          : "bg-transparent"
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
          <div className="w-11 h-11 overflow-hidden rounded-full bg-[#8B1E2D] flex-shrink-0 flex items-center justify-center ring-1 ring-black/5">
            <img
              src={LOGO_URL}
              alt="Leo Club of Mumbai Carter Road"
              className="w-full h-full object-cover"
              style={{ transform: "scale(1.32)" }}
            />
          </div>
          <div className="leading-tight hidden sm:block">
            <div className="font-playfair font-semibold text-[15px] text-[#2C2C2C]">
              Leo Club
            </div>
            <div className="font-inter text-[10px] tracking-[0.2em] uppercase text-[#555555]">
              Mumbai · Carter Road
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              className={({ isActive }) =>
                `relative font-inter text-sm tracking-wide transition-colors ${
                  isActive
                    ? "text-[#8B1E2D] after:w-full"
                    : "text-[#2C2C2C] hover:text-[#8B1E2D] after:w-0 hover:after:w-full"
                } after:content-[''] after:absolute after:-bottom-1 after:left-0 after:h-[1px] after:bg-[#8B1E2D] after:transition-all after:duration-300`
              }
              data-testid={`nav-link-${l.label.toLowerCase()}`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/join"
            className="hidden lg:inline-flex btn-glow bg-[#8B1E2D] text-white px-6 py-2.5 rounded-full font-inter text-sm tracking-wide hover:bg-[#721825]"
            data-testid="nav-join-cta"
          >
            Join Us
          </Link>
          <button
            className="lg:hidden w-10 h-10 flex items-center justify-center text-[#2C2C2C]"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-white border-t border-black/5 px-6 py-8" data-testid="mobile-menu">
          <div className="flex flex-col gap-5">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `font-playfair text-2xl ${isActive ? "text-[#8B1E2D]" : "text-[#2C2C2C]"}`
                }
                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/join"
              className="mt-3 inline-flex w-fit bg-[#8B1E2D] text-white px-6 py-3 rounded-full font-inter text-sm"
              data-testid="mobile-join-cta"
            >
              Join Us
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
